import { useState } from "react";
import { CheckCircle2, Download, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";

export default function GuidePage() {
  const { lang, tr } = useLang();
  const g = tr("guide");
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useSEO("guidePage");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.consent) {
      toast.error(g.errConsent);
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/guide/request", { name: form.name, email: form.email, lang });
      setDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || g.errGeneric);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="relative overflow-hidden bg-navy px-6 pb-20 pt-36 noise lg:px-10 lg:pt-44">
        <div className="pointer-events-none absolute right-0 top-0 h-[460px] w-[460px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div>
            <Reveal>
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold">
                <span className="h-px w-10 bg-gold/70" /> {g.eyebrow}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 max-w-xl font-serif text-4xl leading-[1.08] tracking-tight text-ivory sm:text-5xl lg:text-6xl" data-testid="guide-hero-title">
                {g.title}
              </h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-ivory/60">{g.subtitle}</p>
            </Reveal>
            <Reveal delay={0.26}>
              <ul className="mt-9 space-y-3.5" data-testid="guide-benefits">
                {g.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-ivory/75">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold-bright" /> {b}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.34}>
              <p className="mt-10 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold/80">
                <ShieldCheck className="h-4 w-4" /> {g.trust}
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="relative mx-auto w-full max-w-[320px] rotate-1 rounded-sm border border-gold/30 bg-navy-light p-8 shadow-[0_30px_80px_rgba(0,0,0,0.45)] transition-transform duration-500 hover:rotate-0" data-testid="guide-mock-cover">
              <div className="h-1 w-14 bg-gold" />
              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">LB Trade Advisory</p>
              <p className="mt-5 font-serif text-4xl leading-[1.05] text-gold-bright">Incoterms 2020</p>
              <p className="mt-4 text-[13px] leading-relaxed text-ivory/60">{g.coverTagline}</p>
              <div className="mt-8 space-y-2 border-t border-gold/20 pt-5">
                {(g.coverItems || []).map((x) => (
                  <p key={x} className="font-mono text-[9px] uppercase tracking-[0.18em] text-ivory/40">— {x}</p>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy-light px-6 py-16 lg:px-10 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">{g.insideTitle}</p>
              <div className="mt-8 space-y-6">
                {g.inside.map((item, i) => (
                  <div key={item.t} className="flex gap-5 border-t border-gold/15 pt-6">
                    <span className="font-serif text-2xl text-gold/70">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-serif text-lg text-ivory">{item.t}</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-ivory/50">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-10 border-t border-gold/15 pt-6 text-sm italic leading-relaxed text-ivory/50">{g.authorBlurb}</p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="border border-gold/25 bg-navy p-8 lg:p-10" data-testid="guide-form-card">
              {!done ? (
                <form onSubmit={submit}>
                  <h2 className="font-serif text-2xl text-ivory sm:text-3xl">{g.formTitle}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/55">{g.formSub}</p>
                  <label className="mt-8 block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{g.nameLabel}</span>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="guide-name-input"
                      placeholder={g.namePlaceholder}
                      className="mt-2 w-full border border-gold/25 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/25 transition-colors focus:border-gold/60 focus:outline-none"
                    />
                  </label>
                  <label className="mt-5 block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{g.emailLabel} *</span>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="guide-email-input"
                      placeholder="nome@azienda.com"
                      className="mt-2 w-full border border-gold/25 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/25 transition-colors focus:border-gold/60 focus:outline-none"
                    />
                  </label>
                  <label className="mt-6 flex cursor-pointer items-start gap-3" data-testid="guide-consent-row">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                      data-testid="guide-consent-checkbox"
                      className="mt-1 h-4 w-4 shrink-0 accent-gold"
                    />
                    <span className="text-xs leading-relaxed text-ivory/50">{g.consent}</span>
                  </label>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="guide-submit-button"
                    className="mt-8 inline-flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-navy transition-all hover:bg-gold-bright disabled:opacity-50"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {g.submit}
                  </button>
                  <p className="mt-4 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-ivory/30">{g.noSpam}</p>
                </form>
              ) : (
                <div className="text-center" data-testid="guide-success">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-gold-bright" />
                  <h2 className="mt-5 font-serif text-2xl text-ivory sm:text-3xl" data-testid="guide-success-title">{g.successTitle}</h2>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-ivory/60">{g.successText}</p>
                  <a
                    href={done.download_url}
                    data-testid="guide-download-button"
                    className="mt-8 inline-flex items-center gap-3 bg-gold px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-navy transition-colors hover:bg-gold-bright"
                  >
                    <Download className="h-4 w-4" /> {g.downloadBtn}
                  </a>
                  {done.confirm_sent && (
                    <p className="mt-6 border-t border-gold/20 pt-5 text-xs leading-relaxed text-ivory/45">
                      {g.confirmNote}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
